import { Component, EventEmitter, Input, OnChanges, Output } from "@angular/core";
import { ProgressCategory } from "@common/ADAPT.Common.Model/organisation/progress-category";
import { ProgressItem, ProgressItemBreezeModel } from "@common/ADAPT.Common.Model/organisation/progress-item";
import { ProgressItemStatus, ProgressItemStatusMetadata } from "@common/ADAPT.Common.Model/organisation/progress-item-status";
import { ProgressProductCode, ProgressProductCodeMetadata } from "@common/ADAPT.Common.Model/organisation/progress-product-code";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { BreezeEntityUtilities } from "@common/lib/data/breeze-entity-utilities";
import { CommonDataService } from "@common/lib/data/common-data.service";
import { RxjsBreezeService } from "@common/lib/data/rxjs-breeze.service";
import { ArrayUtilities } from "@common/lib/utilities/array-utilities";
import { ErrorHandlingUtilities } from "@common/lib/utilities/error-handling-utilities";
import { SortUtilities } from "@common/lib/utilities/sort-utilities";
import { ProgressService } from "@common/progress/progress.service";
import { AdaptCommonDialogService } from "@common/ux/adapt-common-dialog/adapt-common-dialog.service";
import { BaseComponent } from "@common/ux/base.component/base.component";
import { EditSimpleValueBreezeEntityDialogComponent, IEditSimpleEntity, IEditSimpleValueBreezeEntityDialogData, SimpleValueType } from "@common/ux/edit-simple-value-breeze-entity-dialog/edit-simple-value-breeze-entity-dialog.component";
import { ValueChangedEvent as DateValueChangedEvent } from "devextreme/ui/date_box";
import { ValueChangedEvent } from "devextreme/ui/select_box";
import { ReorderEvent } from "devextreme/ui/sortable";
import { ContentReadyEvent, ValueChangedEvent as TextValueChangedEvent } from "devextreme/ui/text_box";
import { DxDateBoxComponent, DxSelectBoxComponent } from "devextreme-angular";
import { of, Subject } from "rxjs";
import { catchError, debounceTime, delay, filter, switchMap, tap } from "rxjs/operators";

enum SaveStatus {
    PendingSave,
    Saving,
    Saved,
    Error,
}

@Component({
    selector: "adapt-edit-progress-category",
    standalone: false,
    templateUrl: "./edit-progress-category.component.html",
    styleUrl: "./edit-progress-category.component.scss",
})
export class EditProgressCategoryComponent extends BaseComponent implements OnChanges {
    public readonly SaveStatus = SaveStatus;
    public readonly ProgressItemStatus = ProgressItemStatus;
    public readonly ProgressItemStatuses = ProgressItemStatusMetadata.All;
    public readonly ProgressItemStatusMeta = ProgressItemStatusMetadata.ByStatus;

    @Input({ required: true }) public category!: ProgressCategory;
    @Input() public autoSave = true;

    // cannot use authorisation service here as this is in common.
    @Input() public canEdit = false;

    @Output() public categoryDelete = new EventEmitter<ProgressCategory>();
    @Output() public categoryEdit = new EventEmitter<ProgressCategory>();

    public saveStatus = SaveStatus.Saved;
    public saveStatusTextShown = true;
    public errorMessage?: string;

    public progressItems: ProgressItem[] = [];
    private removedItems: ProgressItem[] = [];

    public triggerSave$ = new Subject<void>();
    public triggerImmediateSave$ = new Subject<void>();

    public constructor(
        private commonDataService: CommonDataService,
        private dialogService: AdaptCommonDialogService,
        rxjsBreezeService: RxjsBreezeService,
    ) {
        super();

        this.triggerImmediateSave$.pipe(
            tap(() => this.saveStatus = SaveStatus.PendingSave),
            switchMap(() => this.save()),
            this.takeUntilDestroyed(),
        ).subscribe();

        this.triggerSave$.pipe(
            filter(() => this.autoSave),
            tap(() => this.saveStatus = SaveStatus.PendingSave),
            debounceTime(2_500),
        ).subscribe(this.triggerImmediateSave$);

        rxjsBreezeService.entityTypeChanged(ProgressItem).pipe(
            debounceTime(100),
            filter((item) => item.progressCategoryId === this.category.progressCategoryId),
            tap(() => this.updateData()),
            this.takeUntilDestroyed(),
        ).subscribe();
    }

    public get categoryColour() {
        const code = this.category.progressProduct.code as ProgressProductCode;
        if (code && Object.values(ProgressProductCode).includes(code)) {
            const meta = ProgressProductCodeMetadata.ByCode[code];
            return meta.categoryColours[this.category.ordinal]
                ?? meta.categoryColours[0]
                ?? undefined;
        }

        return undefined;
    }

    public get progressPercent() {
        return ProgressService.getProgressPercent(this.progressItems);
    }

    public get progressText() {
        return ProgressService.getTooltipTextForItems(this.progressItems);
    }

    public get isOrganisationProduct() {
        return !!this.category.progressProduct.organisationId;
    }

    public get entitiesAreUnmodifiedOrInvalid() {
        return BreezeEntityUtilities.entitiesAreUnmodifiedOrInvalid(this.entitiesToConfirm);
    }

    public get entitiesAreUnmodified() {
        return BreezeEntityUtilities.entitiesAreUnmodified(this.entitiesToConfirm);
    }

    private get entitiesToConfirm() {
        return [...this.progressItems, ...this.removedItems];
    }

    public ngOnChanges() {
        this.updateData();

        setTimeout(() => this.saveStatusTextShown = false, 5_000);
    }

    @Autobind
    public createNewProgressItem(progressCategory: ProgressCategory) {
        return this.commonDataService.create(ProgressItemBreezeModel, {
            progressCategory,
            ordinal: this.getLastItemOrdinal(progressCategory) + 1,
            status: ProgressItemStatus.ToDo,
        }).pipe(
            tap((progressItem) => {
                this.progressItems.push(progressItem);
                this.triggerSave$.next();
            }),
        );
    }

    public onReorder(e: ReorderEvent) {
        // only reorder if actually changed
        if (e.fromIndex !== e.toIndex) {
            SortUtilities.reorderItemInIntegerSortedArray(this.progressItems, "ordinal", e.fromIndex!, e.toIndex!);
            this.triggerSave$.next();
        }
    }

    @Autobind
    public deleteProgressItem(progressItem: ProgressItem) {
        const isAdded = progressItem.entityAspect.entityState.isAdded();

        const remove$ = this.commonDataService.remove(progressItem).pipe(
            tap(() => {
                ArrayUtilities.removeElementFromArray(progressItem, this.progressItems);
                SortUtilities.updateIntegerSortedArrayAfterItemRemoval(this.progressItems, "ordinal", progressItem.ordinal);
                if (!isAdded) {
                    ArrayUtilities.addElementIfNotAlreadyExists(this.removedItems, progressItem);
                }
            }),
        );

        // don't need to confirm deletion of new items
        if (isAdded) {
            return remove$.pipe(
                tap(() => this.triggerSave$.next()),
            );
        }

        return this.dialogService.openConfirmationDialog({
            title: "Delete task",
            message: `Are you sure you want to delete the "${progressItem.name}" task?`,
            confirmButtonPreset: "deleteAndSave",
        }).pipe(
            switchMap(() => remove$),
            switchMap(() => this.commonDataService.saveEntities([progressItem, ...this.progressItems])),
            tap(() => this.postSave()),
            catchError(() => this.dialogService.handleSaveFailure([progressItem, ...this.progressItems])),
        );
    }

    @Autobind
    public editItemComment(progressItem: ProgressItem) {
        const entities: IEditSimpleEntity[] = [{
            entity: progressItem,
            fieldName: "comment",
            label: "Comment",
            type: SimpleValueType.RichText,
            usePropertyValidator: true,
        }];

        return this.dialogService.open(EditSimpleValueBreezeEntityDialogComponent, {
            title: `Comment for "${progressItem.name}"`,
            entities,
            saveOnClose: true,
        } as IEditSimpleValueBreezeEntityDialogData).pipe(
            tap(() => this.postSave()),
        );
    }

    public onTextBoxContentReady(event: ContentReadyEvent, item: ProgressItem) {
        if (item.entityAspect.entityState.isAdded() && this.progressItems.indexOf(item) === this.progressItems.length - 1) {
            event.component.focus();
            // focusing with code doesn't add this class?
            const elm = jQuery(event.element).get(0);
            if (elm) {
                elm.classList.add("dx-state-focused");
            }
        }
    }

    public focusOnClick(component: DxDateBoxComponent | DxSelectBoxComponent) {
        component.instance.focus();
        component.instance.open();
    }

    public onTextBoxChange(item: ProgressItem, event: TextValueChangedEvent) {
        // only trigger save if user changed the value themselves
        if (event.event) {
            item.name = event.value;
            this.triggerSave$.next();
        }
    }

    public onSelectBoxChange(item: ProgressItem, event: ValueChangedEvent) {
        // only trigger save if user changed the value themselves
        if (event.event) {
            item.status = event.value as ProgressItemStatus;
            this.triggerSave$.next();
        }
    }

    public onDateBoxChange(item: ProgressItem, event: DateValueChangedEvent) {
        // only trigger save if user changed the value themselves
        if (event.event) {
            item.date = event.value as Date;
            this.triggerSave$.next();
        }
    }

    private getLastItemOrdinal(progressCategory: ProgressCategory) {
        return progressCategory.progressItems.reduce((max, item) => Math.max(max, item.ordinal), -1);
    }

    private updateData() {
        this.progressItems = [...this.category.progressItems]
            .filter((item) => !item.entityAspect.entityState.isDetached())
            .sort(SortUtilities.getSortByFieldFunction("ordinal"));
        this.removedItems = this.removedItems.filter((item) => !item.entityAspect.entityState.isDetached());
    }

    @Autobind
    public save() {
        return of(undefined).pipe(
            tap(() => this.saveStatus = SaveStatus.Saving),
            // always save items, just skip invalid ones
            switchMap(() => this.commonDataService.saveEntities(this.entitiesToConfirm.filter((e) => !e.entityAspect.hasValidationErrors))),
            delay(500),
            tap(() => {
                this.errorMessage = undefined;
                this.saveStatus = SaveStatus.Saved;
                this.saveStatusTextShown = true;
            }),
            tap(() => setTimeout(() => this.saveStatusTextShown = false, 5_000)),
            tap(() => {
                if (!this.autoSave) {
                    this.updateData();
                }
            }),
            catchError((err) => {
                this.saveStatus = SaveStatus.Error;
                this.errorMessage = ErrorHandlingUtilities.getHttpResponseMessage(err);
                this.log.error(this.errorMessage);
                // continue this observable
                return of();
            }),
            this.takeUntilDestroyed(),
        );
    }

    @Autobind
    public cancel() {
        return this.commonDataService.rejectChanges(this.entitiesToConfirm).pipe(
            tap(() => this.updateData()),
        );
    }

    private postSave() {
        // if we don't have any changed entities after this, reset the save status
        if (!this.commonDataService.hasChanges()) {
            this.saveStatus = SaveStatus.Saved;
        }

        this.updateData();
    }
}
